import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiValidatorTooltipService } from '@fi-shared/fi-tooltip/fi-validator-tooltip.service';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { isDefined } from '@uirouter/core';
import { AppTextService } from 'Source/app/shared/app-text';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { FiTooltipBase } from 'Source/app/shared/fi-tooltip/fi-tooltip-base';

@Component({
    selector: 'fi-content-validator-tooltip',
    templateUrl: '../fi-tooltip.component.html',
    styleUrls: ['./fi-content-validator-tooltip.component.scss', '../fi-tooltip-content.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-content-validator-tooltip' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiContentValidatorTooltipComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiContentValidatorTooltipComponent,
        },
    ],
    standalone: false,
})
export class FiContentValidatorTooltipComponent
    extends FiTooltipBase
    implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
    // tooltip will be shown via subject
    @Input() showErrorTooltip$: Observable<void>;

    // tooltip will be shown via validation function and also ngModel is required
    // validateFn invoked by FiValidatingFormDirective.submit etc
    @Input() validateFn: (item: any) => ValidationErrors;

    @Input() showErrorBorder: boolean = true;
    @Input() showErrorTooltip: boolean = true;
    @Input() preventTooltipDisappear: boolean = false;
    @Input() toggleTooltipVisibility$: Observable<boolean>;
    @Input() preventTooltipOverflowDialog: boolean = false;

    constructor(injector: Injector) {
        super(injector);
    }

    ngOnInit() {
        super.ngOnInit();
        this.setServices();
        if (isDefined(this.toggleTooltipVisibility$)) {
            this.toggleTooltipVisibility$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((isVisible) => {
                if (isVisible) {
                    this.showTooltipErrorMessage({ hasErrors: true, isTouched: true });
                } else {
                    this.hideTooltip();
                }
            });
        }

        if (isDefined(this.showErrorTooltip$)) {
            this.showErrorTooltip$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
                this.showTooltipErrorMessage({ hasErrors: true, isTouched: true });
                if (!this.preventTooltipDisappear) {
                    setTimeout(() => {
                        this.hideTooltip();
                    }, 5000);
                }
            });
        }
    }

    protected getTooltipContentClasses(): string[] {
        const classes = super.getTooltipContentClasses();
        classes.push(...['fi-content-validator-tooltip-content', FiTooltipContentType[this.contentType].toLowerCase()]);
        if (this.preventTooltipOverflowDialog) {
            classes.push('prevent-tooltip-overflow-dialog');;
        }
        return classes;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (this.validateFn) {
            const validationResult = this.validateFn(control.value);
            let hasErrors = false;
            // todo copy paste
            for (let prop in validationResult) {
                if (validationResult[prop] === true) {
                    hasErrors = true;
                } else {
                    delete validationResult[prop];
                }
            }
            this.showTooltipErrorMessage({ hasErrors: hasErrors, isTouched: control.touched });
            return validationResult;
        }
    }
    //registerOnValidatorChange?(fn: () => void): void {    }
    writeValue(obj: any): void {}
    registerOnChange(fn: any): void {}
    registerOnTouched(fn: any): void {}
    //setDisabledState?(isDisabled: boolean): void {}

    protected showTooltipErrorMessage(args: { hasErrors: boolean; isTouched: boolean }) {
        this.fiValidatorTooltipService.showTooltipErrorMessage({
            hasErrors: args.hasErrors,
            isTouched: args.isTouched,
            showErrorBorder: this.showErrorBorder,
            showErrorTooltip: this.showErrorTooltip,
            fiTooltipRef: this,
            renderer: this.renderer,
            contentElement: this.getContentElement(),
            cdr: this.cdr,
        });
    }

    protected fiValidatorTooltipService: FiValidatorTooltipService;
    protected setServices() {
        super.setServices();
        this.fiValidatorTooltipService = this.injector.get(FiValidatorTooltipService);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    protected readonly FiTooltipContentType = FiTooltipContentType;
}
